import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FinanceNextMonthDto } from '../../../../common/interfaces/invoicing.interface';
import { FINANCE_CONTRACTS } from '../../../../common/constants/routes.constants';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { cardSx } from './DebtDonut';
import { monthKeyOf, monthName } from './monthText';
import { groupQuery, KINDERGARTEN_SCOPE, OverviewScope } from './scope';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, '& + &': { borderTop: 1, borderColor: 'line' } }}>
    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: 14, textAlign: 'right' }}>{children}</Box>
  </Box>
);

/**
 * Next month from today, as the contracts stand: roughly what it will invoice (fixed items,
 * less discounts, without attendance), who is ready, and who came and went. Always the real
 * next month, whatever month the page is looking at - it says so in the title.
 */
const NextMonthCard: React.FC<{ data: FinanceNextMonthDto | null; kindergartenId: string; scope?: OverviewScope }> = ({
  data,
  kindergartenId,
  scope = KINDERGARTEN_SCOPE
}) => {
  const { t } = useTranslation();
  if (!data) return <Box sx={cardSx} />;
  const name = monthName(monthKeyOf(data.year, data.month));
  return (
    <Box component="section" aria-labelledby="next-month-title" sx={cardSx}>
      <Typography id="next-month-title" component="h2" sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
        {t('finance.nextMonth.title', { month: name })}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>≈ {formatMoney(data.expectedNet)}</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>{t('finance.nextMonth.expected')}</Typography>
      <Row label={t('finance.nextMonth.ready')}>{t('finance.nextMonth.readyOf', { ready: data.readyCount, count: data.totalCount })}</Row>
      {data.notReadyCount > 0 && (
        <Row label={t('finance.nextMonth.notReady')}>
          <Box component="span" sx={{ color: 'warning.main' }}>
            {data.notReadyCount}
            {data.notReady.length > 0 &&
              ` · ${data.notReady.map(entry => `${entry.name.split(' ')[0]}: ${t(`finance.todo.reasons.${entry.reason ?? 'price'}`)}`).join(', ')}`}
          </Box>
        </Row>
      )}
      {data.discountTotal > 0 && (
        <Row label={t('finance.nextMonth.discounts')}>
          <Box component="span" sx={{ color: 'success.main', fontVariantNumeric: 'tabular-nums' }}>
            −{formatMoney(data.discountTotal)}
          </Box>
        </Row>
      )}
      <Row label={t('finance.nextMonth.moves')}>
        +{data.joined} / −{data.left}
      </Row>
      <Link
        component={RouterLink}
        to={`${FINANCE_CONTRACTS}?kindergartenId=${kindergartenId}${groupQuery(scope)}`}
        underline="hover"
        sx={{ display: 'inline-block', mt: 1.5, fontSize: 14, color: 'primary.main' }}
      >
        {t('finance.nextMonth.link')} →
      </Link>
    </Box>
  );
};

export default NextMonthCard;
